import { Spacer } from "@vericus/cadmus-ui";
import { type ManualPresetKey } from "@vericus/cadmus-manual";
import { show } from "@ebay/nice-modal-react";

import { AuthorityState } from "@/features/authority";
import { TaskNoticeFragment } from "@/generated/graphql";
import { useInstitution } from "@/graphql/hooks";

import { AcademicIntegrityAgreement } from "./AcademicIntegrityAgreement";
import {
  clearStaleAcceptance,
  removeGenAiAcceptance,
  setGenAiAccepted,
} from "./gen-ai-acceptance-storage";
import { ConnectedGenAiGuidelinesModal } from "./gen-ai-guidelines-modal";
import { GenerativeAiAgreement } from "./generative-ai-agreement";

export interface SubmissionDeclarationProps {
  /**
   * ID used to scope GenAI notice acceptance in localStorage. Typically the
   * work ID, but may be any stable unique identifier for the work context.
   */
  acceptanceScopeId: string;
  /**
   * Use the short version of the pledge.
   */
  withShortenedPledge?: boolean;
  /**
   * Whether the student has agreed to the submission pledges.
   */
  hasCheckedPledges: AuthorityState["hasAcceptedSubmissionDeclarations"];
  /**
   * Callback to update the checked value of a submission declaration agreement.
   */
  onUpdateSubmissionDeclaration: (
    declarationType: keyof AuthorityState["hasAcceptedSubmissionDeclarations"],
    hasAgreed: boolean
  ) => void;
  /**
   * Whether the generative AI agreement is required.
   */
  requireGenAiAgreement?: boolean;
  /**
   * GenAI guideline notice attached to the task, if any.
   */
  genaiGuidelineNotice?: TaskNoticeFragment;
  /**
   * Callback to open the manual preset viewer.
   */
  onOpenManualPreset: (key: ManualPresetKey) => void;
}

/**
 * Provides the academic integrity and generative AI agreements for the student
 * to agree to before their work can be submitted.
 * Academic Integrity agreement is always required while Generative AI agreement is
 * optional, as per the requirement of the tenant.
 * The Academic Integrity Agreement will use custom institution declaration if available in
 * the graphql cache.
 */
export function SubmissionDeclaration(props: SubmissionDeclarationProps) {
  const { data } = useInstitution();
  const customAcademicIntegrityDeclarationText =
    data.customDeclarationText ?? undefined;

  return (
    <>
      <AcademicIntegrityAgreement
        customDeclarationText={customAcademicIntegrityDeclarationText}
        isChecked={props.hasCheckedPledges.academicIntegrity}
        onUpdateAgreement={(hasAccepted) =>
          props.onUpdateSubmissionDeclaration("academicIntegrity", hasAccepted)
        }
        {...props}
      />
      <Spacer spacing={16} />
      {props.requireGenAiAgreement === true && (
        <>
          <GenerativeAiAgreement
            isChecked={props.hasCheckedPledges.generativeAi}
            onUpdateAgreement={(checked) => {
              if (props.genaiGuidelineNotice) {
                if (checked) {
                  clearStaleAcceptance(props.acceptanceScopeId, props.genaiGuidelineNotice.id);
                  setGenAiAccepted(props.acceptanceScopeId, props.genaiGuidelineNotice.id);
                } else {
                  removeGenAiAcceptance(
                    props.acceptanceScopeId,
                    props.genaiGuidelineNotice.id
                  );
                }
              }
              props.onUpdateSubmissionDeclaration("generativeAi", checked);
            }}
            onOpenAdditionalInstructions={
              props.genaiGuidelineNotice
                ? () =>
                    show(ConnectedGenAiGuidelinesModal, {
                      content: props.genaiGuidelineNotice!.content,
                      acceptanceScopeId: props.acceptanceScopeId,
                      taskNoticeId: props.genaiGuidelineNotice!.id,
                    })
                : undefined
            }
            onOpenUniversityGuidelines={() =>
              props.onOpenManualPreset("generative_ai")
            }
          />
          <Spacer spacing={16} />
        </>
      )}
    </>
  );
}
